import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  ChevronLeftRounded as PreviousIcon,
  ChevronRightRounded as NextIcon,
  RefreshRounded as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Header from '../common/Header';
import Sidebar, { DEFAULT_SIDEBAR_PROPS } from '../common/Sidebar';
import MapView from '../map/MapView';
import type { ViewportOcclusionRect } from '../../models/types';
import type { TripResponse, TripSummaryResponse } from '../../api/routingJobsApi';
import { routingJobsApi } from '../../api/routingJobsApi';
import { useHub } from '../../context/hub';
import { buildJumboBuyExecutionPreview } from '../../modules/jumbobuy/executionPreviewAdapter';
import { buildExecutionDayMetrics, sortExecutionTrips } from './executionModel';
import type {
  ExecutionActivityViewModel,
  ExecutionPhase,
  ExecutionTripSort,
} from './executionTypes';
import ExecutionTripDrawer from './ExecutionTripDrawer';
import ExecutionTripDetailsCard from './ExecutionTripDetailsCard';
import ExecutionComparisonTimeline from './ExecutionComparisonTimeline';
import ExecutionMapLayerControls from './ExecutionMapLayerControls';
import { buildExecutionMapOverlay, DEFAULT_EXECUTION_MAP_LAYERS } from './executionMapModel';
import { toTripsTaskInfo } from './tripMapAdapter';

const DEFAULT_TIMEZONE = 'Asia/Kolkata';
const DETAIL_CONCURRENCY = 4;
const ALL_PHASES: ExecutionPhase[] = ['ACTIVE', 'DELAYED', 'PENDING', 'COMPLETED'];

const readStoredNumber = (key: string, fallback: number): number => {
  if (typeof window === 'undefined') return fallback;
  const value = Number(window.localStorage.getItem(key));
  return Number.isFinite(value) && value > 0 ? value : fallback;
};

const readStoredBoolean = (key: string, fallback: boolean): boolean => {
  if (typeof window === 'undefined') return fallback;
  const value = window.localStorage.getItem(key);
  return value == null ? fallback : value === 'true';
};

const loadDetailsBounded = async (
  depotId: string,
  summaries: TripSummaryResponse[],
  cache: Map<string, TripResponse>,
  force: boolean
): Promise<{ details: TripResponse[]; failed: number }> => {
  const results: Array<TripResponse | null> = new Array(summaries.length).fill(null);
  let cursor = 0;
  let failed = 0;

  const worker = async () => {
    while (cursor < summaries.length) {
      const index = cursor;
      cursor += 1;
      const summary = summaries[index];
      const cacheKey = `${depotId}:${summary.id}`;
      try {
        const cached = cache.get(cacheKey);
        const detail = !force && cached ? cached : await routingJobsApi.getTrip(depotId, summary.id);
        cache.set(cacheKey, detail);
        results[index] = detail;
      } catch {
        failed += 1;
      }
    }
  };

  await Promise.all(Array.from(
    { length: Math.min(DETAIL_CONCURRENCY, summaries.length) },
    () => worker()
  ));
  return { details: results.filter((detail): detail is TripResponse => Boolean(detail)), failed };
};

const TripsConsole: React.FC = () => {
  const { hubId, tripId } = useParams<{ hubId: string; tripId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const { hubs, currentHubId, changeHub } = useHub();
  const resolvedDepotId = hubId && hubId !== 'default' ? hubId : currentHubId;
  const depot = hubs.find((candidate) => candidate.hubId === resolvedDepotId);
  const depotTimezone = depot?.configFilesMap?.timezone || DEFAULT_TIMEZONE;
  const [date, setDate] = useState(() => searchParams.get('date') || todayInTimezone(depotTimezone));
  const [details, setDetails] = useState<TripResponse[]>([]);
  const [activeTripId, setActiveTripId] = useState<string | null>(tripId?.toUpperCase() || null);
  const [detailsOpen, setDetailsOpen] = useState(Boolean(tripId));
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [activeActivityId, setActiveActivityId] = useState<string | null>(null);
  const [hoveredActivityId, setHoveredActivityId] = useState<string | null>(null);
  const [hoveredTripId, setHoveredTripId] = useState<string | null>(null);
  const [mapFocusedTripId, setMapFocusedTripId] = useState<string | null>(null);
  const [mapLayers, setMapLayers] = useState(DEFAULT_EXECUTION_MAP_LAYERS);
  const [detailsOcclusion, setDetailsOcclusion] = useState<ViewportOcclusionRect | null>(null);
  const [timelineOcclusion, setTimelineOcclusion] = useState<ViewportOcclusionRect | null>(null);
  const [visibleTripIds, setVisibleTripIds] = useState<string[]>([]);
  const [query, setQuery] = useState(tripId?.toUpperCase() || '');
  const [sort, setSort] = useState<ExecutionTripSort>('attention');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [phases, setPhases] = useState<ExecutionPhase[]>(ALL_PHASES);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partialFailureCount, setPartialFailureCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(() => readStoredBoolean('explan:trips-drawer-open', true));
  const [drawerWidth, setDrawerWidth] = useState(() => readStoredNumber('explan:trips-drawer-width', 408));
  const [focusTripRequest, setFocusTripRequest] = useState<{ tripName: string; requestId: number } | null>(null);
  const [focusTouchPointRequest, setFocusTouchPointRequest] = useState<{ lngLat: [number, number]; requestId: number } | null>(null);
  const detailCacheRef = useRef(new Map<string, TripResponse>());
  const previousLoadedDateRef = useRef<string | null>(null);
  const previousTripIdsRef = useRef<string[]>([]);
  const activeTripIdRef = useRef<string | null>(tripId?.toUpperCase() || null);
  const timelineOpenRef = useRef(false);
  const requestCounterRef = useRef(0);

  useEffect(() => {
    if (hubId && hubId !== 'default' && hubId !== currentHubId) changeHub(hubId);
  }, [changeHub, currentHubId, hubId]);

  useEffect(() => {
    window.localStorage.setItem('explan:trips-drawer-open', String(drawerOpen));
  }, [drawerOpen]);

  useEffect(() => {
    window.localStorage.setItem('explan:trips-drawer-width', String(Math.round(drawerWidth)));
  }, [drawerWidth]);

  useEffect(() => {
    activeTripIdRef.current = activeTripId;
  }, [activeTripId]);

  useEffect(() => {
    timelineOpenRef.current = timelineOpen;
  }, [timelineOpen]);

  const loadDate = useCallback(async (forceDetails = false, background = false) => {
    if (!resolvedDepotId) return;
    if (background) setRefreshing(true); else setLoading(true);
    setError(null);
    setPartialFailureCount(0);
    try {
      const summaries = await routingJobsApi.listTrips(resolvedDepotId, date, 'CREATED');
      const result = await loadDetailsBounded(resolvedDepotId, summaries, detailCacheRef.current, forceDetails);
      setDetails(result.details);
      const ids = result.details.map((detail) => detail.id);
      const changedDate = previousLoadedDateRef.current !== date;
      previousLoadedDateRef.current = date;
      const previousIds = previousTripIdsRef.current;
      previousTripIdsRef.current = ids;
      setMapFocusedTripId((current) => current && ids.includes(current) ? current : null);
      setVisibleTripIds((current) => changedDate
        ? ids
        : Array.from(new Set([
          ...current.filter((id) => ids.includes(id)),
          ...ids.filter((id) => !previousIds.includes(id)),
        ])));
      if (!tripId && activeTripIdRef.current && !ids.includes(activeTripIdRef.current)) {
        activeTripIdRef.current = null;
        setActiveTripId(null);
        setDetailsOpen(false);
        setTimelineOpen(false);
      }
      if (result.failed) {
        setPartialFailureCount(result.failed);
        setError(`${result.failed} Trip ${result.failed === 1 ? 'detail' : 'details'} could not be loaded. Showing available Trips.`);
      }
    } catch (requestError: any) {
      setDetails([]);
      setVisibleTripIds([]);
      setError(requestError?.response?.data?.message || 'Unable to load Trips for this date.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // Trip selection changes do not invalidate the selected day's cached list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, isNarrow, resolvedDepotId]);

  useEffect(() => {
    loadDate(false, false);
  }, [loadDate]);

  useEffect(() => {
    if (!resolvedDepotId || !tripId) return;
    let cancelled = false;
    const requestedId = tripId.toUpperCase();
    const cacheKey = `${resolvedDepotId}:${requestedId}`;
    const loadExact = async () => {
      try {
        const cached = detailCacheRef.current.get(cacheKey);
        const detail = cached ?? await routingJobsApi.getTrip(resolvedDepotId, requestedId);
        if (cancelled) return;
        detailCacheRef.current.set(cacheKey, detail);
        setActiveTripId(detail.id);
        setDetailsOpen(isNarrow ? !timelineOpenRef.current : true);
        if (isNarrow) setDrawerOpen(false);
        setQuery(detail.id);
        if (detail.dispatchDate !== date) {
          setDate(detail.dispatchDate);
          navigate(`/trips/hub/${resolvedDepotId}/trip/${detail.id}?date=${detail.dispatchDate}`, { replace: true });
        }
      } catch (requestError: any) {
        if (!cancelled) setError(requestError?.response?.status === 404
          ? `Trip ${requestedId} was not found in this depot.`
          : requestError?.response?.data?.message || 'Unable to load the Trip.');
      }
    };
    loadExact();
    return () => { cancelled = true; };
  }, [date, isNarrow, navigate, resolvedDepotId, tripId]);

  const executionTrips = useMemo(
    () => details.map((detail, index) => buildJumboBuyExecutionPreview(detail, index)),
    [details]
  );
  const executionById = useMemo(() => new Map(executionTrips.map((trip) => [trip.id, trip])), [executionTrips]);
  const activeTrip = activeTripId ? executionById.get(activeTripId) ?? null : null;
  const taskInfo = useMemo(() => toTripsTaskInfo(details), [details]);
  const headerMetrics = useMemo(() => buildExecutionDayMetrics(executionTrips), [executionTrips]);
  const visibleDetailsIds = useMemo(() => visibleTripIds.filter((id) => executionById.has(id)), [executionById, visibleTripIds]);
  const mapEmphasisTripId = (detailsOpen || timelineOpen) ? activeTripId : mapFocusedTripId;
  const executionOverlay = useMemo(() => buildExecutionMapOverlay(
    executionTrips,
    visibleDetailsIds,
    mapEmphasisTripId,
    mapLayers,
    activeActivityId
  ), [activeActivityId, executionTrips, mapEmphasisTripId, mapLayers, visibleDetailsIds]);

  useEffect(() => {
    setActiveActivityId(activeTrip?.currentActivityId ?? null);
    setHoveredActivityId(null);
  }, [activeTrip?.currentActivityId, activeTripId]);

  const displayedTrips = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = executionTrips.filter((trip) => {
      if (!phases.includes(trip.phase)) return false;
      if (!normalizedQuery) return true;
      const values = [
        trip.id,
        trip.plannedTrip.plannedRouteId,
        trip.plannedTrip.vehicle.number,
        trip.plannedTrip.vehicle.type,
        trip.plannedTrip.driver.name,
        ...trip.activities.flatMap((activity) => [activity.title, activity.plannedReference || '']),
      ];
      return values.some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
    });
    const sorted = sortExecutionTrips(filtered, sort);
    return sortDirection === 'desc' ? sorted : [...sorted].reverse();
  }, [executionTrips, phases, query, sort, sortDirection]);

  const changeDate = (nextDate: string) => {
    if (!resolvedDepotId || !nextDate) return;
    setDate(nextDate);
    setActiveTripId(null);
    setDetailsOpen(false);
    setTimelineOpen(false);
    setActiveActivityId(null);
    setMapFocusedTripId(null);
    setQuery('');
    navigate(`/trips/hub/${resolvedDepotId}?date=${nextDate}`);
  };

  const shiftDate = (days: number) => {
    const value = new Date(`${date}T00:00:00Z`);
    value.setUTCDate(value.getUTCDate() + days);
    changeDate(value.toISOString().slice(0, 10));
  };

  const openTrip = (id: string) => {
    if (!resolvedDepotId) return;
    setMapFocusedTripId(null);
    setActiveTripId(id);
    setDetailsOpen(true);
    if (isNarrow) {
      setTimelineOpen(false);
      setDrawerOpen(false);
    }
    navigate(`/trips/hub/${resolvedDepotId}/trip/${id}?date=${date}`, { replace: true });
  };

  const openTripTimeline = (id: string) => {
    if (!resolvedDepotId) return;
    setMapFocusedTripId(null);
    setActiveTripId(id);
    timelineOpenRef.current = true;
    setTimelineOpen(true);
    if (isNarrow) {
      setDetailsOpen(false);
      setDrawerOpen(false);
    }
    navigate(`/trips/hub/${resolvedDepotId}/trip/${id}?date=${date}`, { replace: true });
  };

  const findExactTrip = async () => {
    if (!resolvedDepotId || !query.trim()) return;
    const id = query.trim().toUpperCase();
    setRefreshing(true);
    setError(null);
    setPartialFailureCount(0);
    try {
      const detail = await routingJobsApi.getTrip(resolvedDepotId, id);
      detailCacheRef.current.set(`${resolvedDepotId}:${detail.id}`, detail);
      setDate(detail.dispatchDate);
      setMapFocusedTripId(null);
      setActiveTripId(detail.id);
      setDetailsOpen(true);
      setTimelineOpen(false);
      if (isNarrow) setDrawerOpen(false);
      setQuery(detail.id);
      navigate(`/trips/hub/${resolvedDepotId}/trip/${detail.id}?date=${detail.dispatchDate}`);
    } catch (requestError: any) {
      setError(requestError?.response?.status === 404
        ? `Trip ${id} was not found in this depot.`
        : requestError?.response?.data?.message || 'Unable to find the Trip.');
    } finally {
      setRefreshing(false);
    }
  };

  const toggleVisibility = (id: string) => setVisibleTripIds((current) => (
    current.includes(id) ? current.filter((candidate) => candidate !== id) : [...current, id]
  ));

  const focusTrip = (id: string) => {
    setVisibleTripIds((current) => current.includes(id) ? current : [...current, id]);
    setMapFocusedTripId(id);
    requestCounterRef.current += 1;
    setFocusTripRequest({ tripName: id, requestId: requestCounterRef.current });
  };

  const focusActivity = (activity: ExecutionActivityViewModel) => {
    setActiveActivityId(activity.id);
    requestCounterRef.current += 1;
    setFocusTouchPointRequest({ lngLat: [...activity.location] as [number, number], requestId: requestCounterRef.current });
  };

  const togglePhase = (phase: ExecutionPhase) => setPhases((current) => (
    current.includes(phase) ? current.filter((candidate) => candidate !== phase) : [...current, phase]
  ));

  const viewportHeight = typeof window === 'undefined' ? 900 : window.innerHeight;
  const viewportWidth = typeof window === 'undefined' ? 1440 : window.innerWidth;
  const effectiveDrawerWidth = isMedium ? Math.min(drawerWidth, 380) : drawerWidth;
  const drawerOcclusion: ViewportOcclusionRect[] = drawerOpen ? [isNarrow ? {
    left: 48,
    top: viewportHeight - Math.min(620, viewportHeight * 0.62),
    right: viewportWidth,
    bottom: viewportHeight,
    width: Math.max(0, viewportWidth - 48),
    height: Math.min(620, viewportHeight * 0.62),
  } : {
    left: 48,
    top: 44,
    right: 48 + effectiveDrawerWidth,
    bottom: viewportHeight,
    width: effectiveDrawerWidth,
    height: Math.max(0, viewportHeight - 44),
  }] : [];
  const mapOcclusions = [
    ...drawerOcclusion,
    ...(detailsOpen && detailsOcclusion ? [detailsOcclusion] : []),
    ...(timelineOpen && timelineOcclusion ? [timelineOcclusion] : []),
  ];

  const handleMapActivityClick = useCallback((nextTripId: string, activityId: string) => {
    const trip = executionById.get(nextTripId);
    const activity = trip?.activities.find((candidate) => candidate.id === activityId);
    if (!trip || !activity) return;
    openTrip(nextTripId);
    focusActivity(activity);
  // openTrip and focusActivity intentionally operate on the latest render state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionById]);

  const handleMapActivityHover = useCallback((_tripId: string | null, activityId: string | null) => {
    setHoveredActivityId(activityId);
    setHoveredTripId(_tripId);
  }, []);

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerOpen(open);
    if (open && isNarrow) {
      setDetailsOpen(false);
      setTimelineOpen(false);
    }
  };

  const headerAddon = (
    <Stack direction="row" spacing={0.35} alignItems="center" sx={{ ml: { xs: 0.25, sm: 1.25 } }}>
      <Tooltip title="Previous day"><IconButton size="small" aria-label="Previous day" onClick={() => shiftDate(-1)}><PreviousIcon /></IconButton></Tooltip>
      <TextField
        size="small"
        type="date"
        value={date}
        onChange={(event) => changeDate(event.target.value)}
        inputProps={{ 'aria-label': 'Dispatch date' }}
        sx={{ width: { xs: 124, sm: 142 }, '& .MuiInputBase-input': { py: 0.55, fontSize: 12 } }}
      />
      <Tooltip title="Next day"><IconButton size="small" aria-label="Next day" onClick={() => shiftDate(1)}><NextIcon /></IconButton></Tooltip>
      <Tooltip title="Refresh Trips">
        <span>
          <IconButton size="small" aria-label="Refresh Trips" disabled={refreshing} onClick={() => loadDate(true, true)}>
            <RefreshIcon sx={{ animation: refreshing ? 'spin 0.9s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column' }}>
      <Header
        taskInfo={taskInfo}
        loading={loading}
        isTaskSummary
        titleOverride="Track Trips"
        showHubSelector
        showMetrics
        metricStripItems={headerMetrics}
        headerAddon={headerAddon}
        onHubChange={(nextHubId) => navigate(`/trips/hub/${nextHubId}?date=${date}`)}
      />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar
          {...DEFAULT_SIDEBAR_PROPS}
          activeItem="trips"
          onRoutePlanClick={() => navigate(`/task-summary/hub/${resolvedDepotId || 'default'}`, { replace: true })}
          onControlTowerClick={() => navigate('/control-tower')}
          onUserManagementClick={() => navigate(`/user-management/hub/${resolvedDepotId || 'default'}`)}
          onUserInfoClick={() => navigate(`/user-info/hub/${resolvedDepotId || 'default'}`)}
        />
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', bgcolor: '#eef2f5' }}>
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
            {taskInfo ? (
              <MapView
                taskInfo={taskInfo}
                visibleTrips={visibleDetailsIds}
                showPolygons={false}
                showSequence={false}
                allowRouteLines
                defaultShowRoutes
                showMapLegend={false}
                showMapOptions={false}
                showTripVehicleMarkers={false}
                editEnabled={false}
                editMode={false}
                selectedTouchPoints={[]}
                onMapOptionsChange={() => undefined}
                onTouchPointSelect={() => undefined}
                onCancelEdit={() => undefined}
                onSubmitEdit={() => undefined}
                activeTripName={mapEmphasisTripId}
                focusTripRequest={focusTripRequest}
                focusTouchPointRequest={focusTouchPointRequest}
                focusOcclusionRects={mapOcclusions}
                executionOverlay={executionOverlay}
                executionHoveredActivityId={hoveredActivityId}
                onExecutionActivityClick={handleMapActivityClick}
                onExecutionActivityHover={handleMapActivityHover}
                onExecutionTripClick={(id) => {
                  openTrip(id);
                  focusTrip(id);
                }}
              />
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
                <Stack alignItems="center" spacing={0.75}>
                  {loading ? <Typography variant="body2">Loading execution workspace…</Typography> : <Typography variant="body2">No Trips available for {date}.</Typography>}
                  <Typography variant="caption">The map remains the primary workspace when Trips are available.</Typography>
                  {!loading ? <Button size="small" variant="outlined" onClick={() => loadDate(true, false)}>Retry / refresh</Button> : null}
                </Stack>
              </Box>
            )}
          </Box>

          <ExecutionMapLayerControls value={mapLayers} onChange={setMapLayers} />

          <ExecutionTripDrawer
            trips={displayedTrips}
            activeTripId={activeTripId}
            highlightedTripId={hoveredTripId}
            visibleTripIds={visibleTripIds}
            query={query}
            sort={sort}
            sortDirection={sortDirection}
            phases={phases}
            loading={loading}
            onQueryChange={(value) => setQuery(value.toUpperCase())}
            onFindExact={findExactTrip}
            onSortChange={setSort}
            onSortDirectionChange={setSortDirection}
            onTogglePhase={togglePhase}
            onOpenTrip={openTrip}
            onToggleVisibility={toggleVisibility}
            onFocusTrip={focusTrip}
            onOpenTimeline={openTripTimeline}
            onShowAll={() => setVisibleTripIds(executionTrips.map((trip) => trip.id))}
            onHideAll={() => setVisibleTripIds([])}
            open={drawerOpen}
            onOpenChange={handleDrawerOpenChange}
            width={effectiveDrawerWidth}
            onWidthChange={setDrawerWidth}
            bottomSheet={isNarrow}
          />

          {detailsOpen && activeTrip ? (
            <ExecutionTripDetailsCard
              trip={activeTrip}
              drawerWidth={drawerOpen ? effectiveDrawerWidth : 48}
              onClose={() => setDetailsOpen(false)}
              onFocusActivity={focusActivity}
              activeActivityId={activeActivityId}
              hoveredActivityId={hoveredActivityId}
              onHoverActivity={setHoveredActivityId}
              onOpenTimeline={() => {
                setTimelineOpen(true);
                if (isNarrow) setDetailsOpen(false);
              }}
              onOcclusionRectChange={setDetailsOcclusion}
              bottomSheet={isNarrow}
            />
          ) : null}

          {timelineOpen && activeTrip ? (
            <ExecutionComparisonTimeline
              trip={activeTrip}
              drawerWidth={drawerOpen ? effectiveDrawerWidth : 48}
              activeActivityId={activeActivityId}
              hoveredActivityId={hoveredActivityId}
              onClose={() => setTimelineOpen(false)}
              onFocusActivity={focusActivity}
              onHoverActivity={setHoveredActivityId}
              onOcclusionRectChange={setTimelineOcclusion}
              bottomSheet={isNarrow}
            />
          ) : null}

          {error ? (
            <Alert
              severity={partialFailureCount > 0 ? 'warning' : 'error'}
              onClose={() => setError(null)}
              action={<Button color="inherit" size="small" onClick={() => loadDate(true, true)}>Retry</Button>}
              sx={{ position: 'absolute', right: 16, top: 62, zIndex: 50, maxWidth: 520, boxShadow: 4 }}
            >
              {error}
            </Alert>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const todayInTimezone = (timezone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: DEFAULT_TIMEZONE }).format(new Date());
  }
};

export default TripsConsole;
